/**
 * Simple client-side router
 */

import { stripBasePath, withBasePath } from './basePath';

interface Route {
  path: string;
  title: string;
  render: () => Promise<void>;
}

const routes: Route[] = [];
let currentRoute: string = '/';

/**
 * Register a route
 */
export function registerRoute(path: string, title: string, render: () => Promise<void>) {
  routes.push({ path, title, render });
}

/**
 * Navigate to a route
 */
export function navigate(path: string) {
  window.history.pushState({}, '', withBasePath(path));
  handleRoute();
}

/**
 * Initialize router
 */
export function initRouter() {
  // Register all routes
  registerRoutes();

  // Handle initial route
  handleRoute();

  // Handle browser back/forward
  window.addEventListener('popstate', handleRoute);

  // Handle link clicks
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a[data-link]');

    if (link) {
      e.preventDefault();
      const href = link.getAttribute('href');
      if (href) {
        navigate(href);
      }
    }
  });
}

/**
 * Handle current route
 */
async function handleRoute() {
  const path = stripBasePath(window.location.pathname);
  currentRoute = path;

  // Find matching route
  const route = routes.find((r) => r.path === path) || routes.find((r) => r.path === '/');

  if (route) {
    // Update page title
    document.title = `${route.title} | Valley Eats Driver Portal`;

    // Render the route
    try {
      await route.render();
    } catch (error) {
      console.error('Error rendering route:', error);
      showError('Failed to load page');
    }
  }
}

/**
 * Register all application routes
 */
function registerRoutes() {
  registerRoute('/', 'Dashboard', async () => {
    const { renderDashboardPage } = await import('../pages/dashboard');
    renderDashboardPage();
  });

  registerRoute('/deliveries', 'Deliveries', async () => {
    const { renderDeliveriesPage } = await import('../pages/deliveries');
    renderDeliveriesPage();
  });

  registerRoute('/schedule', 'Schedule', async () => {
    const { renderSchedulePage } = await import('../pages/schedule');
    renderSchedulePage();
  });

  registerRoute('/training', 'Training', async () => {
    const { renderTrainingPage } = await import('../pages/training');
    renderTrainingPage();
  });

  registerRoute('/bulletins', 'Bulletins', async () => {
    const { renderBulletinsPage } = await import('../pages/bulletins');
    renderBulletinsPage();
  });

  registerRoute('/profile', 'Profile', async () => {
    const { renderProfilePage } = await import('../pages/profile');
    renderProfilePage();
  });

  registerRoute('/points-shop', 'Points Shop', async () => {
    const { renderPointsShopPage } = await import('../pages/points-shop');
    renderPointsShopPage();
  });

  registerRoute('/app', 'VE Driver App', async () => {
    const { renderAppDownloadPage } = await import('../pages/app-download');
    renderAppDownloadPage();
  });
}

/**
 * Show error message
 */
function showError(message: string) {
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = `
      <div class="container" style="padding-top: 100px;">
        <div class="card" style="max-width: 500px; margin: 0 auto; text-align: center;">
          <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
          <p style="color: var(--slate);">${message}</p>
        </div>
      </div>
    `;
  }
}

/**
 * Get current route
 */
export function getCurrentRoute(): string {
  return currentRoute;
}
